import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styles from './Auth.module.css';
import Input from '../../components/Input';
import OtpInput from '../../components/OtpInput';
import Button from '../../components/Button';
import PasswordStrength from '../../components/PasswordStrength';
import { useAuth } from '../../store/AuthContext';
import { useToast } from '../../store/ToastContext';
import { validators, validateForm } from '../../utils/validation';
import { CheckCircleIcon } from '../../components/Icons';

function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const { resetPassword, resendOtp } = useAuth();
  const toast = useToast();
  
  const email = location.state?.email;
  const [step, setStep] = useState('otp'); // 'otp' or 'password'
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState(false);
  
  const [formData, setFormData] = useState({
    new_password: '',
    confirm_password: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (!email) {
      navigate('/forgot-password');
    }
  }, [email, navigate]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleOtpSubmit = (e) => {
    e.preventDefault();
    
    if (otp.length !== 6) {
      setOtpError(true);
      return;
    }

    setOtpError(false);
    setStep('password');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateForm(formData, {
      new_password: [validators.required, validators.password],
      confirm_password: [validators.required, validators.confirmPassword(formData.new_password)],
    });

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setLoading(true);
    try {
      await resetPassword({
        email,
        otp_code: otp,
        new_password: formData.new_password,
        confirm_password: formData.confirm_password,
      });
      setSuccess(true);
    } catch (error) {
      // If OTP is invalid, go back to OTP step
      if (error.message.toLowerCase().includes('otp') || error.message.toLowerCase().includes('code')) {
        setStep('otp');
        setOtpError(true);
      }
      toast.error(error.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await resendOtp(email, 'forgot_password');
      toast.success('OTP sent to your email');
      setCountdown(60);
      setOtp('');
      setOtpError(false);
    } catch (error) {
      toast.error(error.message || 'Failed to resend OTP');
    } finally {
      setResending(false);
    }
  };

  if (success) {
    return (
      <div className={styles.formContainer}>
        <div className={styles.successContainer}>
          <div className={styles.successIcon}>
            <CheckCircleIcon size={48} />
          </div>
          <h2 className={styles.formTitle}>Password reset</h2>
          <p className={styles.formSubtitle}>
            Your password has been successfully reset
          </p>
          <Button onClick={() => navigate('/login')}>
            Sign in
          </Button>
        </div>
      </div>
    );
  }

  // Step 1: Enter OTP
  if (step === 'otp') {
    return (
      <div className={styles.formContainer}>
        <div className={styles.formHeader}>
          <h2 className={styles.formTitle}>Enter verification code</h2>
          <p className={styles.formSubtitle}>
            Enter the 6-digit code sent to<br />
            <strong>{email}</strong>
          </p>
        </div>

        <form onSubmit={handleOtpSubmit} className={styles.form}>
          <div className={styles.otpWrapper}>
            <OtpInput
              length={6}
              value={otp}
              onChange={setOtp}
              error={otpError}
            />
            {otpError && (
              <p className={styles.errorText}>Invalid or expired code. Please try again.</p>
            )}
          </div>

          <Button type="submit" fullWidth disabled={otp.length !== 6}>
            Continue
          </Button>

          <div className={styles.resendContainer}>
            {countdown > 0 ? (
              <span className={styles.countdown}>Resend code in {countdown}s</span>
            ) : (
              <button
                type="button"
                className={styles.resendButton}
                onClick={handleResend}
                disabled={resending}
              >
                {resending ? 'Sending...' : 'Resend code'}
              </button>
            )}
          </div>
        </form>

        <p className={styles.formFooter}>
          <Link to="/login" className={styles.link}>
            Back to login
          </Link>
        </p>
      </div>
    );
  }

  // Step 2: Set new password
  return (
    <div className={styles.formContainer}>
      <div className={styles.formHeader}>
        <h2 className={styles.formTitle}>Reset password</h2>
        <p className={styles.formSubtitle}>Create a new password for your account</p>
      </div>

      <form onSubmit={handlePasswordSubmit} className={styles.form}>
        <div>
          <Input
            label="New Password"
            name="new_password"
            type="password"
            placeholder="Enter new password"
            value={formData.new_password}
            onChange={handleChange}
            error={errors.new_password}
            autoComplete="new-password"
          />
          <PasswordStrength password={formData.new_password} />
        </div>

        <Input
          label="Confirm Password"
          name="confirm_password"
          type="password"
          placeholder="Confirm new password"
          value={formData.confirm_password}
          onChange={handleChange}
          error={errors.confirm_password}
          autoComplete="new-password"
        />

        <Button type="submit" fullWidth loading={loading}>
          Reset Password
        </Button>

        <Button 
          type="button" 
          variant="secondary" 
          fullWidth 
          onClick={() => setStep('otp')}
        >
          Back to OTP
        </Button>
      </form>

      <p className={styles.formFooter}>
        <Link to="/login" className={styles.link}>
          Back to login
        </Link>
      </p>
    </div>
  );
}

export default ResetPassword;
